import React from "react";
import styled from "styled-components";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Spin } from "@arco-design/web-react";
import { AnchorGlyph } from "./AppBarAndDrawer/AppBarAndDrawer";

// The public sign-in page at app.contactanchor.com/login. The marketing site
// (contactanchor.com) links here; the actual credential form is Auth0's Universal
// Login, reached via loginWithRedirect.
//
// Sign-ups are gated in Auth0: its Post-Login Action denies users whose email
// isn't verified or who haven't been approved (app_metadata.approved), and Auth0
// sends them back here with that reason as error_description.

const DENIAL_MESSAGES = {
  email_not_verified: {
    title: "Verify your email",
    body: "We sent you a verification link. Once you've clicked it, log in again.",
  },
  pending_approval: {
    title: "Thanks for signing up",
    body: "Your account is awaiting approval. We review new accounts by hand and will email you once you're in.",
  },
};

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background: var(--ca-hero-bg);
  color: var(--ca-hero-title);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 400px;
  padding: 40px 32px 32px;
  box-sizing: border-box;
  background: var(--color-bg-2);
  color: var(--color-text-1);
  border: 1px solid var(--color-border-1);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(7, 30, 42, 0.12);
  text-align: center;
`;

const Mark = styled.div`
  width: 44px;
  height: 44px;
  margin: 0 auto 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ca-brass);
  color: #fff;
`;

const Brand = styled.div`
  font-family: var(--ca-serif);
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Lede = styled.p`
  margin: 8px 0 28px;
  color: var(--color-text-3);
  font-size: 14px;
`;

const Notice = styled.div`
  margin: 0 0 24px;
  padding: 14px 16px;
  text-align: left;
  border-radius: 8px;
  background: rgb(var(--arcoblue-1));
  border: 1px solid rgb(var(--arcoblue-3));
  strong {
    display: block;
    margin-bottom: 4px;
  }
  p {
    margin: 0;
    color: var(--color-text-2);
    font-size: 13px;
    line-height: 1.5;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Fine = styled.p`
  margin: 24px 0 0;
  font-size: 12px;
  color: var(--color-text-3);
  a {
    color: inherit;
  }
`;

export function SignIn() {
  const { loginWithRedirect, logout, user, isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Page>
        <Spin />
      </Page>
    );
  }
  if (isAuthenticated) {
    // Not an automatic redirect to /home: the app sends people here when the API
    // rejects their token, so bouncing an Auth0-authenticated user straight back
    // would loop. Let them continue or log out instead.
    return (
      <Page>
        <Panel>
          <Mark>
            <AnchorGlyph size={24} />
          </Mark>
          <Brand>Contact Anchor</Brand>
          <Lede>Logged in as {user?.email}</Lede>
          <Stack>
            <Button type="primary" size="large" long onClick={() => navigate("/home")}>
              Continue
            </Button>
            <Button size="large" long onClick={() => navigate("/logout")}>
              Log out
            </Button>
          </Stack>
        </Panel>
      </Page>
    );
  }

  const denial = error && DENIAL_MESSAGES[error.error_description];

  return (
    <Page>
      <Panel>
        <Mark>
          <AnchorGlyph size={24} />
        </Mark>
        <Brand>Contact Anchor</Brand>
        <Lede>Log in to your account</Lede>

        {denial && (
          <Notice role="status">
            <strong>{denial.title}</strong>
            <p>{denial.body}</p>
          </Notice>
        )}
        {error && !denial && (
          <Notice role="alert">
            <strong>We couldn't log you in</strong>
            <p>{error.error_description || error.message}</p>
          </Notice>
        )}

        <Stack>
          <Button type="primary" size="large" long onClick={() => loginWithRedirect()}>
            Log in
          </Button>
          <Button
            size="large"
            long
            onClick={() => loginWithRedirect({ screen_hint: "signup" })}
          >
            Create an account
          </Button>
          {error && (
            // Auth0 still holds a session for the denied user, so "Log in" would
            // silently re-run the same denial. Ending that session lets them pick
            // another account.
            <Button
              type="text"
              long
              onClick={() => logout({ returnTo: window.location.origin + "/login" })}
            >
              Use a different account
            </Button>
          )}
        </Stack>

        <Fine>
          <a href="https://contactanchor.com">contactanchor.com</a>
          {process.env.NODE_ENV === "development" && (
            <>
              {" · "}
              <a href="/local-login">Dev login</a>
            </>
          )}
        </Fine>
      </Panel>
    </Page>
  );
}

// /signup — the marketing site's "Get started" link. Goes straight to Auth0's
// sign-up screen rather than making the visitor click through /login.
export function SignUp() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ screen_hint: "signup" });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  return (
    <Page>
      <Spin />
    </Page>
  );
}
